import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { getRecentConversationId } from '../../lib/conversations';
import { floatedMode } from '../../lib/chat-nav';
import { isDuplicateHandoff, type ChatHandoff, type HandoffStamp } from '../../lib/chat-handoff';
import type { PickedContact } from '../../lib/contact-references';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';

/**
 * The active conversation, shared across routes. The home chat derives its
 * conversation from the URL (?conversation=…), but when Orion navigates the
 * user to another screen (an app page, tasks, …) the URL no longer carries the
 * conversation — yet the floating chat widget must keep talking in the SAME
 * thread. This store is the route-surviving source of truth the widget reads.
 *
 * It also owns the chat's SIZE (`chatMode`). Size is intentionally NOT derived
 * from the route: navigating must be able to PRESERVE the current size (a menu
 * click leaves a floating panel floating; a conversation switch keeps the chat
 * at whatever size it's at). So it's explicit shared state, mutated by a small
 * set of actions:
 *   - maximizeChat()         — the bubble, "new conversation" → the side dock (`expanded`)
 *   - minimizeChat()         — the brand logo + a client-control navigate to
 *                              home: collapse the chat to its bubble
 *   - floatChatIfMaximized() — a menu/nav click: shrink a maximized chat (side
 *                              dock `expanded` OR `fullscreen`) to a panel,
 *                              otherwise leave it alone
 *   - floatChatIfMinimized() — a conversation switch: surface a minimized chat
 *                              as a panel, otherwise leave it alone
 *   - setChatMode()          — the chat's own maximize/float/minimize buttons,
 *                              client-control ("navigate and shrink" → panel)
 *
 * Persisted to sessionStorage so a reload on a non-home route still resumes the
 * right thread (the ephemeral navigate command itself is one-shot — see the
 * design doc).
 */
// `fullscreen` is Symphony-only: the side-docked chat grown to fill the whole
// viewport (the "expand" control). Orion never sets it (its FloatingChat only
// uses bubble/panel/expanded), so adding it to the union is a no-op there.
export type ChatMode = 'bubble' | 'panel' | 'expanded' | 'fullscreen';

// A composer action the home launcher wants the chat to perform — but can't run
// itself, because the chat (and its attachment/voice infra) is unmounted while
// it's a bubble. The launcher stashes the intent here and opens the chat; the
// chat consumes it on mount. See ChatSurface.
export type PendingChatAction = 'voice' | 'attach';

// A pre-filled message handed to the chat to auto-send once it opens — the
// imperative counterpart to the cross-route `chatHandoffLink` URL handoff. A
// surface (a task card, a detail panel) opens the chat IN PLACE with this
// message instead of routing anywhere or stamping query params, the same way
// TaskSidePanel opens a docked panel over the current page. Transient (in
// memory, never in the URL); ChatSurface consumes it, then clears it.
export type PendingPrefill = {
  message: string;
  conversationId: string | null;
  /**
   * Attachments the handoff carries, already uploaded. Today that is exactly one thing: a VOICE
   * NOTE recorded on the home launcher (see `openChatWithVoiceNote`), whose wire shape is an EMPTY
   * `message` plus the audio attachment — so this is the one handoff where a blank message still
   * sends. The launcher uploads before handing over, because a `File` cannot ride a URL and the
   * recorded duration has to be stamped on the attachment.
   */
  attachments?: MessageAttachment[];
  /** The handoff's hidden context line, sent as the stream endpoint's top-level `context` alongside the
   *  message. See `ChatHandoffNew.context` — this is the phones' `body.context` channel, on web at last. */
  context?: string;
  /** The task card this handoff came from, sent as `references.taskId`. Unlike `context` this rides BOTH
   *  handoff kinds: a resolve CTA continues the task's own thread when it has one, and mints a fresh one
   *  when it does not, and the card is the same either way. */
  taskId?: string;
};

interface ActiveConversationValue {
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  chatMode: ChatMode;
  setChatMode: (mode: ChatMode) => void;
  // Open a fresh, maximized Maestro chat and KEEP it expanded — overrides the
  // home route's "empty expanded chat self-collapses" rule, since this is a
  // deliberate open (the Talk to Maestro rail entry), not an incidental handoff.
  openMaestroChat: () => void;
  // Mark the already-open chat as a deliberate expanded surface WITHOUT changing
  // its size — for the header's "New chat", which clears the thread in place (see
  // useStartNewConversation). Unlike openMaestroChat it must keep the chat at
  // whatever size it's at; without this the resulting empty landing would trip
  // the home-route self-collapse and drop the user back to the bubble.
  retainChatExpanded: () => void;
  // Open the chat IN PLACE on the current page with a pre-filled, auto-sending
  // message — a fresh thread (`kind: 'new'`) or an existing one. The imperative,
  // URL-free replacement for the chat-handoff link, mirroring TaskSidePanel.openTask.
  openChatWithMessage: (handoff: ChatHandoff) => void;
  /**
   * Open a FRESH chat that auto-sends a just-recorded voice note — the home launcher's half of the
   * composer mic. Mirrors the phones' `homeVoiceSendTapped`, which also resets to a new chat and
   * sends the audio with empty text rather than continuing the recent thread.
   *
   * Deliberately NOT `openChatWithMessage({ message: '' })`: that path's double-click latch keys on
   * the message text, so two takes in quick succession would collapse into one and the second
   * recording would be silently dropped.
   */
  openChatWithVoiceNote: (attachment: MessageAttachment) => void;
  // The pending auto-send handoff, or null. ChatSurface reads it to seed the
  // prefill/autosend, then calls consumePrefill once it has fired.
  pendingPrefill: PendingPrefill | null;
  consumePrefill: () => void;
  // Open a conversation as a transient PEEK (the home morning brief): expands the
  // chat onto that thread, but remembers whichever thread was active before, so
  // closing the chat (minimize to bubble) returns to the last chat instead of
  // stranding the user on the brief. Switching to another thread mid-peek cancels
  // the restore.
  openBriefConversation: (id: string) => void;
  // The transiently-peeked brief thread, or null. The URL writer skips it so it's
  // never stamped into `?conversation` / sessionStorage.
  briefPeekId: string | null;
  // Whether the active thread was deliberately opened (belongs in the URL) vs.
  // ambiently resumed (context + sessionStorage only). Symphony's URL writer gates
  // `?conversation` on this so the address bar isn't stamped all the time.
  conversationInUrl: boolean;
  keepChatExpanded: boolean;
  maximizeChat: () => void;
  minimizeChat: () => void;
  floatChatIfMaximized: () => void;
  floatChatIfMinimized: () => void;
  pendingChatAction: PendingChatAction | null;
  requestChatAction: (action: PendingChatAction) => void;
  clearChatAction: () => void;
  // Files picked from the home launcher (its own OS dialog, no navigation) and
  // handed to the chat to upload once it mounts — so attaching doesn't first
  // jump to a redundant chat screen.
  pendingUploadFiles: File[] | null;
  requestUpload: (files: File[]) => void;
  clearUpload: () => void;
  // Contacts picked from the home launcher's "+ Contact" picker, handed to the chat to fold into its
  // own picked-contacts state once it mounts — the same cross-navigation bridge `pendingUploadFiles`
  // uses, since a contact reference can't ride the plain-text `chatHandoffLink` URL.
  pendingContacts: PickedContact[] | null;
  requestContacts: (contacts: PickedContact[]) => void;
  clearPendingContacts: () => void;
}

const ActiveConversationContext = createContext<ActiveConversationValue | null>(null);
const STORAGE_KEY = 'orion-web:active-conversation';
const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;

export function ActiveConversationProvider({ children }: { children: ReactNode }) {
  const [activeConversationId, setState] = useState<string | null>(null);
  const userActedRef = useRef(false);
  // Latest active id, readable synchronously (openBriefConversation captures the
  // pre-peek thread without re-subscribing to state).
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeConversationId;
  // The morning-brief PEEK — a thread shown transiently WITHOUT persisting it as the
  // resumable pointer, so it never lands in sessionStorage or the URL and closing /
  // reloading returns to the last real chat instead of the brief. `null` = not
  // peeking. Exposed so the single URL writer (FloatingChat) skips it too.
  const [briefPeekId, setBriefPeekId] = useState<string | null>(null);
  const briefPeekIdRef = useRef<string | null>(null);
  briefPeekIdRef.current = briefPeekId;
  // The real thread to return to when the peek closes.
  const restoreOnCloseRef = useRef<string | null>(null);
  // Whether the ACTIVE thread was deliberately opened (sidebar click, deep link,
  // handoff, a thread the user is chatting in) vs. ambiently resumed from
  // sessionStorage / the recent list. Only a deliberate open belongs in the URL —
  // resume lives in context + sessionStorage, so the address bar isn't stamped with
  // `?conversation` all the time. Symphony's URL writer reads this; ambient stays clean.
  const [conversationInUrl, setConversationInUrl] = useState(false);
  // Size starts collapsed; the deep-link / handoff URL (read on mount by the
  // chat widget) and the actions above take it from there. Not persisted — a
  // reload resolves the size from the URL's chat intent instead.
  const [chatMode, setChatMode] = useState<ChatMode>('bubble');

  // Hydrate after mount (sessionStorage is client-only). Survives route changes
  // within the tab and a reload on a non-home route.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // A brand-new user has no stored/recent thread to resume, so the onboarding kickoff (derived from
    // recipient state, no `?welcome=true` param) starts clean on its own. A mid-onboarding returner's
    // stored/recent pointer IS their onboarding thread — resuming it here is correct.
    const params = new URL(window.location.href).searchParams;
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setState(stored);
      return;
    }
    if (params.get('conversation') || params.get('new') === '1' || params.get('prefill')) return;
    let cancelled = false;
    void getRecentConversationId(RESUME_WINDOW_MS).then((id) => {
      if (cancelled || !id || userActedRef.current) return;
      window.sessionStorage.setItem(STORAGE_KEY, id);
      setState(id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setActiveConversationId = useCallback((id: string | null) => {
    // Any explicit selection ends a brief peek and persists normally — the user
    // deliberately chose this thread, so it becomes the resumable pointer AND
    // belongs in the URL (deep-link / share). Selecting "no thread" clears both.
    setBriefPeekId(null);
    restoreOnCloseRef.current = null;
    setConversationInUrl(id != null);
    userActedRef.current = true;
    setState(id);
    if (typeof window === 'undefined') return;
    if (id) window.sessionStorage.setItem(STORAGE_KEY, id);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const [pendingChatAction, setPendingChatAction] = useState<PendingChatAction | null>(null);

  // Deliberate "open the chat expanded" intent (Talk to Maestro). While true, the
  // chat's empty-expanded self-collapse on the home route is suppressed. It clears
  // the moment the chat leaves that state — the user floats/minimizes it, or a
  // real conversation starts — so it never sticks around suppressing a genuine
  // collapse later.
  const [keepChatExpanded, setKeepChatExpanded] = useState(false);
  const openMaestroChat = useCallback(() => {
    setActiveConversationId(null);
    setChatMode('expanded');
    setKeepChatExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const retainChatExpanded = useCallback(() => setKeepChatExpanded(true), []);
  useEffect(() => {
    // A deliberately-opened Maestro chat stays "kept expanded" across BOTH open
    // sizes (side dock `expanded` and Symphony `fullscreen`), so clicking expand
    // on an empty new chat doesn't trip the empty-chat self-collapse.
    if ((chatMode !== 'expanded' && chatMode !== 'fullscreen') || activeConversationId) setKeepChatExpanded(false);
  }, [chatMode, activeConversationId]);

  // The imperative side-chat handoff: point the store at the target thread (or a
  // fresh one), expand the chat over the current page, and stash the message for
  // ChatSurface to auto-send. `keepChatExpanded` for a new thread suppresses the
  // empty-chat self-collapse until the send lands (same rule openMaestroChat uses).
  const [pendingPrefill, setPendingPrefill] = useState<PendingPrefill | null>(null);
  // Double-click latch: an identical handoff re-fired inside the window is dropped —
  // each `kind: 'new'` handoff would otherwise reset the thread and mint one more
  // conversation per click (see isDuplicateHandoff; same 1.5s rule as the native apps).
  const lastHandoffRef = useRef<HandoffStamp | null>(null);
  const openChatWithMessage = useCallback((handoff: ChatHandoff) => {
    const conversationId = handoff.kind === 'existing' ? handoff.conversationId : null;
    const stamp: HandoffStamp = { message: handoff.message, conversationId, at: Date.now() };
    if (isDuplicateHandoff(lastHandoffRef.current, stamp)) return;
    lastHandoffRef.current = stamp;
    setActiveConversationId(conversationId);
    setChatMode('expanded');
    setKeepChatExpanded(conversationId === null);
    setPendingPrefill({
      message: handoff.message,
      conversationId,
      // Only a NEW-thread handoff carries hidden context today, which is what the union already says.
      ...(handoff.kind === 'new' && handoff.context ? { context: handoff.context } : {}),
      ...(handoff.taskId ? { taskId: handoff.taskId } : {}),
    });
  }, [setActiveConversationId]);
  const consumePrefill = useCallback(() => setPendingPrefill(null), []);

  const openChatWithVoiceNote = useCallback((attachment: MessageAttachment) => {
    setActiveConversationId(null);
    setChatMode('expanded');
    setKeepChatExpanded(true);
    setPendingPrefill({ message: '', conversationId: null, attachments: [attachment] });
  }, [setActiveConversationId]);

  const openBriefConversation = useCallback((id: string) => {
    // Only capture the restore target on the FIRST peek — re-peeking must not
    // overwrite it with the brief id itself.
    if (briefPeekIdRef.current === null) restoreOnCloseRef.current = activeIdRef.current;
    setBriefPeekId(id);
    userActedRef.current = true;
    setState(id); // show it, but DON'T persist — sessionStorage keeps the last real chat
    setChatMode('expanded');
  }, []);

  // Closing the chat (minimize to bubble) while peeking the brief returns to the
  // thread that was active before the peek — so the default chat is the last chat,
  // not the brief. sessionStorage still holds that thread (the peek never wrote it).
  useEffect(() => {
    if (chatMode !== 'bubble' || briefPeekIdRef.current === null) return;
    const restore = restoreOnCloseRef.current;
    setBriefPeekId(null);
    restoreOnCloseRef.current = null;
    setState(restore);
  }, [chatMode]);

  const maximizeChat = useCallback(() => setChatMode('expanded'), []);
  const minimizeChat = useCallback(() => setChatMode('bubble'), []);
  // Float a maximized chat (side dock `expanded` OR `fullscreen`) down to a panel; see `floatedMode`.
  const floatChatIfMaximized = useCallback(() => setChatMode(floatedMode), []);
  const floatChatIfMinimized = useCallback(() => setChatMode((m) => (m === 'bubble' ? 'panel' : m)), []);
  const requestChatAction = useCallback((action: PendingChatAction) => setPendingChatAction(action), []);
  const clearChatAction = useCallback(() => setPendingChatAction(null), []);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[] | null>(null);
  const requestUpload = useCallback((files: File[]) => setPendingUploadFiles(files), []);
  const clearUpload = useCallback(() => setPendingUploadFiles(null), []);
  const [pendingContacts, setPendingContacts] = useState<PickedContact[] | null>(null);
  const requestContacts = useCallback((contacts: PickedContact[]) => setPendingContacts(contacts), []);
  const clearPendingContacts = useCallback(() => setPendingContacts(null), []);

  return (
    <ActiveConversationContext.Provider
      value={{
        activeConversationId,
        setActiveConversationId,
        chatMode,
        setChatMode,
        openMaestroChat,
        retainChatExpanded,
        openChatWithMessage,
        openChatWithVoiceNote,
        pendingPrefill,
        consumePrefill,
        openBriefConversation,
        briefPeekId,
        conversationInUrl,
        keepChatExpanded,
        maximizeChat,
        minimizeChat,
        floatChatIfMaximized,
        floatChatIfMinimized,
        pendingChatAction,
        requestChatAction,
        clearChatAction,
        pendingUploadFiles,
        requestUpload,
        clearUpload,
        pendingContacts,
        requestContacts,
        clearPendingContacts,
      }}
    >
      {children}
    </ActiveConversationContext.Provider>
  );
}

// Tolerate a missing provider (returns a no-op store) so a stray mount never
// throws — the provider is installed once at the app root.
export function useActiveConversation(): ActiveConversationValue {
  return (
    useContext(ActiveConversationContext) ?? {
      activeConversationId: null,
      setActiveConversationId: () => {},
      chatMode: 'bubble',
      setChatMode: () => {},
      openMaestroChat: () => {},
      retainChatExpanded: () => {},
      openChatWithMessage: () => {},
      openChatWithVoiceNote: () => {},
      pendingPrefill: null,
      consumePrefill: () => {},
      openBriefConversation: () => {},
      briefPeekId: null,
      conversationInUrl: false,
      keepChatExpanded: false,
      maximizeChat: () => {},
      minimizeChat: () => {},
      floatChatIfMaximized: () => {},
      floatChatIfMinimized: () => {},
      pendingChatAction: null,
      requestChatAction: () => {},
      clearChatAction: () => {},
      pendingUploadFiles: null,
      requestUpload: () => {},
      clearUpload: () => {},
      pendingContacts: null,
      requestContacts: () => {},
      clearPendingContacts: () => {},
    }
  );
}
